import { useBlocker } from 'react-router';
import { useSuspenseMaxDomainsQuery } from '#api/domains.queries.ts';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { ButtonGroup, ButtonGroupItem } from '#components/patterns/button-group/ButtonGroup.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useUrlParam } from '#hooks/useUrlParam.ts';
import { TooManyOidcProvidersModal } from '../add/TooManyOidcProvidersModal.tsx';
import { DomainList } from '../list/domains/DomainList.tsx';
import { SSOTabs } from '../tabs/SSOTabs.tsx';
import { useSuspenseIsProvidersLimitReached } from '../util/useSuspenseIsProvidersLimitReached.ts';
import { EmptySSOSettings } from './empty/EmptySSOSettings.tsx';
import { useSuspenseAllOIDCProvidersQuery } from './SSOSettingsPage.queries.ts';

export function SSOSettingsPage() {
  const { user } = useAuth();
  const location = useAppLocation();
  const {
    data: {
      admin: { data: adminIDPs },
      endpoint: { data: endpointIDPs },
    },
  } = useSuspenseAllOIDCProvidersQuery();
  const {
    data: { data: domains },
  } = useSuspenseMaxDomainsQuery();
  const [tab] = useUrlParam('tab');

  const { isProvidersLimitReached, endpointProvidersLimit } = useSuspenseIsProvidersLimitReached();
  const blocker = useBlocker(
    ({ nextLocation }) => isProvidersLimitReached && nextLocation.pathname === '/settings/sso/add'
  );

  // Show empty state if they have no providers
  if (adminIDPs.length === 0 && endpointIDPs.length === 0 && domains.length === 0) {
    return <EmptySSOSettings addOIDCHref="add" />;
  }

  // Wait until we know what kind of user this is
  if (!user) return null;

  return (
    <MainLayout
      header={
        <PageHeader title="Single sign-on">
          <ButtonGroup>
            <ButtonGroupItem>
              <LinkButton size="small" to={`/settings/sso/add?type=${tab}`} state={{ from: location }}>
                Add
              </LinkButton>
            </ButtonGroupItem>
          </ButtonGroup>
        </PageHeader>
      }
    >
      <SSOTabs adminIDPs={adminIDPs} endpointIDPs={endpointIDPs} />
      <DomainList className="mt-32" idps={[...adminIDPs, ...endpointIDPs]} />
      {blocker.state === 'blocked' && isProvidersLimitReached ?
        <TooManyOidcProvidersModal onClose={blocker.reset} providersLimit={endpointProvidersLimit} />
      : null}
    </MainLayout>
  );
}
